import React, { useEffect, useRef } from 'react';

import { useLocation, useNavigate } from 'react-router-dom';

import {
	ArrowUpRight,
	ChevronDown,
	CreditCardIcon,
	FileTextIcon,
	Plus,
	SettingsIcon,
	Tag,
	UsersIcon,
} from 'lucide-react';

import { useGetTenantConfigQuery } from '../api/tenants';
import { useToast } from '../context/ToastContext';
import { useSubscription } from '../hooks';
import { useOnboarding } from '../hooks/useOnboarding';
import { useAuth } from '../providers/AppAuthProvider';
import { useTypedSelector } from '../store';
import { hasAnyRole, hasRole } from '../utils/utils';

import { Button } from './common';
import Logo from './Logo';

interface NavigationItem {
	name: string;
	path: string;
	icon?: React.ReactNode;
}

interface LayoutProps {
	children: React.ReactNode;
	showNavigation?: boolean;
}

const formatCreditsText = (credits: number | undefined): string => {
	const count = Math.max(credits ?? 0, 0);
	return `${count} report${count === 1 ? '' : 's'} left`;
};

const Layout: React.FC<LayoutProps> = ({ children, showNavigation = true }) => {
	const navigate = useNavigate();
	const location = useLocation();
	const { user, logout } = useAuth();
	const { showToast } = useToast();
	const { onboardingState } = useOnboarding();

	// Get tenant from Redux state (set by TenantInitializer) or from API query
	const tenantFromRedux = useTypedSelector((state) => state.tenant.tenant);
	const tenantLoading = useTypedSelector((state) => state.tenant.loading);
	const { data: tenantConfig } = useGetTenantConfigQuery(undefined, {
		skip: !!tenantFromRedux, // Skip API call if we already have tenant in Redux
	});
	const tenant = tenantFromRedux || tenantConfig?.tenant;

	// Determine which logo to show
	// Show RocketFee logo only for app.rocketfee.com or rocketfee-dev.mightybot.ai
	// For all other domains, wait for tenant data to load, then show tenant logo if available
	const hostname = window.location.hostname;
	const isRocketFeeDomain =
		hostname === 'app.rocketfee.com' ||
		hostname === 'dev.rocketfee.com' ||
		hostname === 'rocketfee-dev.mightybot.ai';

	// Use tenant logo if available and not on RocketFee domain
	// For non-RocketFee domains, wait for tenant to load before showing logo
	const logoToShow = isRocketFeeDomain
		? null // Always show RocketFee logo for RocketFee domains
		: tenant
			? tenant.logo_base64 || null
			: undefined; // undefined = wait for tenant to load

	// Hide navigation menu items when in onboarding flow, but keep header and user menu
	const isInOnboarding =
		location.pathname.startsWith('/onboarding') && onboardingState.isOnboarding;
	const shouldShowNavigationMenu = showNavigation && !isInOnboarding;
	const [showUserMenu, setShowUserMenu] = React.useState(false);
	const userMenuRef = useRef<HTMLDivElement>(null);
	const [isModalOpen, setIsModalOpen] = React.useState(false);
	const { refetch: refetchSubscription, subscriptionStatus } =
		useSubscription();
	// Check if user has admin/enterprise_admin/super_admin role
	const isTeamAdmin = React.useMemo(() => {
		return hasAnyRole(user?.roles, [
			'admin',
			'enterprise_admin',
			'super_admin',
		]);
	}, [user?.roles]);

	const isSuperAdmin = React.useMemo(() => {
		return hasRole(user?.roles, 'super_admin');
	}, [user?.roles]);

	const isTenantAdmin = React.useMemo(() => {
		return hasRole(user?.roles, 'tenant_admin');
	}, [user?.roles]);

	const canViewAdminPage = React.useMemo(() => {
		return isSuperAdmin || isTenantAdmin;
	}, [isSuperAdmin, isTenantAdmin]);

	React.useEffect(() => {
		const checkForModals = () => {
			const modalOverlays = document.querySelectorAll(
				'[class*="fixed"][class*="inset-0"][class*="z-50"]',
			);
			setIsModalOpen(modalOverlays.length > 0);
		};

		checkForModals();

		const observer = new MutationObserver(checkForModals);
		observer.observe(document.body, {
			childList: true,
			subtree: true,
			attributes: true,
			attributeFilter: ['class'],
		});

		return () => observer.disconnect();
	}, []);

	useEffect(() => {
		function handleClickOutside(event: MouseEvent) {
			if (
				userMenuRef.current &&
				!userMenuRef.current.contains(event.target as Node)
			) {
				setShowUserMenu(false);
			}
		}

		if (showUserMenu) {
			document.addEventListener('mousedown', handleClickOutside);
		}

		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
		};
	}, [showUserMenu]);

	// Get banner message based on credit status
	const getBannerMessage = () => {
		if (!subscriptionStatus) return null;

		const creditsRemaining = subscriptionStatus.credits_remaining || 0;
		const creditsTotal = subscriptionStatus.credits_total || 0;

		// First time user - has 1 free credit available
		if (creditsTotal === 0 && creditsRemaining === -1) {
			return {
				message:
					'Welcome! You can upload 1 statement for free analysis. Get started now!',
				type: 'success',
			};
		}
		// User has uploaded one free document - no more free credits
		else if (creditsTotal === 0 && creditsRemaining === 0) {
			return {
				message:
					'Your free statement analysis has been used. Purchase more credits to continue analyzing documents.',
				type: 'warning',
			};
		}
		// User has paid credits remaining
		else if (creditsRemaining > 0) {
			if (creditsRemaining === 1) {
				return {
					message: `You have 1 statement analysis credit remaining. Purchase more credits to continue analyzing.`,
					type: 'info',
				};
			}
		}
		// User has used all their credits
		else {
			return {
				message:
					"You've used all your credits. Purchase more credits to continue analyzing statements.",
				type: 'warning',
			};
		}
	};

	const bannerInfo = getBannerMessage();

	const shouldShowBanner = bannerInfo && !isInOnboarding;
	const navigationItems: NavigationItem[] = [
		{
			name: 'Reports',
			path: '/',
			icon: <FileTextIcon className="w-5 h-5" />,
		},
		{
			name: 'Subscription',
			path: '/subscription',
			icon: <CreditCardIcon className="w-5 h-5" />,
		},
		...(isTeamAdmin
			? [
					{
						name: 'Team',
						path: '/team',
						icon: <UsersIcon className="w-5 h-5" />,
					} as NavigationItem,
				]
			: []),
		...(isSuperAdmin
			? [
					{
						name: 'Tenants',
						path: '/tenants',
						icon: <UsersIcon className="w-5 h-5" />,
					} as NavigationItem,
				]
			: []),
		{
			name: 'Settings',
			path: '/settings',
			icon: <SettingsIcon className="w-5 h-5" />,
		},
	];

	const isActivePath = (path: string) => {
		if (
			path === '/' &&
			(location.pathname === '/' || location.pathname === '/reports')
		) {
			return true;
		}
		if (path === '/settings' && location.pathname === '/settings') {
			return true;
		}
		return location.pathname === path;
	};

	const handleNavigationClick = (path: string) => {
		navigate(path);
	};

	// Expose refetch function globally for other components to use
	React.useEffect(() => {
		(window as any).refetchSubscription = refetchSubscription;
		return () => {
			delete (window as any).refetchSubscription;
		};
	}, []);

	const handleLogout = async () => {
		try {
			const token = localStorage.getItem('auth_token');
			if (token) {
				await fetch(`${process.env.REACT_APP_STATEMENT_API}/api/auth/logout`, {
					method: 'POST',
					headers: {
						Authorization: `Bearer ${token}`,
						'Content-Type': 'application/json',
					},
				}).catch(() => {});
			}
		} catch (e) {
			// ignore network errors on logout
		} finally {
			logout();
			setShowUserMenu(false);
			navigate('/login');
		}
	};

	const displayName = (user?.name || user?.email || 'User').trim();
	const avatarInitial = displayName ? displayName.charAt(0).toUpperCase() : 'U';

	return (
		<div className="min-h-screen bg-neutral-50 flex flex-col" data-layout-root>
			{/* Header */}
			<header className="bg-white border border-neutral-200 fixed top-0 left-0 right-0 z-50">
				<div className="max-w-8xl mx-auto px-4 sm:px-6 lg:px-8">
					<div className="flex justify-between items-center h-16 relative overflow-visible">
						<div className="w-full flex justify-between items-center">
							<div className="flex items-center gap-7">
								{/* Logo */}
								<div className="flex items-center">
									<div
										onClick={() => navigate('/')}
										className="flex items-center cursor-pointer transition-opacity"
									>
										{logoToShow !== undefined ? (
											<Logo
												logoBase64={logoToShow}
												alt={tenant?.name || 'RocketFee'}
											/>
										) : (
											// Show placeholder while tenant is loading (same size as logo to prevent layout shift)
											<div className="h-10 w-auto" />
										)}
									</div>
								</div>

								{/* Navigation */}
								<div className="flex justify-start items-center">
									{/* Navigation Menu Items (hidden during onboarding) */}
									{shouldShowNavigationMenu &&
										navigationItems.map((item) => (
											<div
												key={item.path}
												className="border-b-1 border-transparent flex justify-center items-center gap-4 cursor-pointer"
											>
												<div
													onClick={() => handleNavigationClick(item.path)}
													className={`h-7 px-4 rounded-md flex justify-center items-center gap-2.5 transition-colors font-primary ${
														isActivePath(item.path)
															? 'bg-violet-100'
															: 'hover:bg-neutral-50'
													}`}
												>
													<div
														className={`justify-center text-sm font-medium leading-normal font-primary ${
															isActivePath(item.path)
																? 'text-black'
																: 'text-neutral-600'
														}`}
													>
														{item.name}
													</div>
												</div>
											</div>
										))}
								</div>
							</div>

							{/* Right side: Credits counter and User Profile */}
							{showNavigation && (
								<div className="flex items-center space-x-4">
									{/* Credits counter - new design */}
									<button
										onClick={() => navigate('/subscription')}
										className="h-7 px-4 py-1 bg-rose-100 rounded-full outline outline-1 outline-offset-[-1px] outline-red-400 flex justify-start items-center gap-1 overflow-hidden hover:bg-rose-200 transition-colors"
										title="Click to manage subscription"
									>
										<div className="text-center justify-start text-neutral-600 text-xs font-medium leading-none font-primary">
											{formatCreditsText(subscriptionStatus?.credits_remaining)}
										</div>
										<ArrowUpRight className="w-4 h-4" />
									</button>

									{/* User Profile - new design */}
									<div className="relative overflow-visible" ref={userMenuRef}>
										<button
											onClick={() => setShowUserMenu((v) => !v)}
											className="flex justify-start items-center gap-[5px] hover:bg-gray-50 rounded-lg px-2 py-1 transition-colors"
											title={displayName}
											aria-label={displayName}
										>
											<div
												data-image="R"
												className="w-8 h-8 relative rounded-full overflow-hidden"
											>
												<div className="w-8 h-8 left-0 top-0 absolute bg-fuchsia-300" />
												<div className="w-2.5 h-3 left-[11.98px] top-[10.48px] absolute text-white text-center flex items-center justify-center text-xs font-bold">
													{avatarInitial}
												</div>
											</div>
											<div className="justify-center text-neutral-800 text-sm font-medium leading-normal font-primary">
												{displayName}
											</div>
											<ChevronDown className="w-4 h-4" />
										</button>
										{showUserMenu && (
											<div className="absolute right-0 mt-2 w-48 bg-white border border-neutral-200 rounded-md shadow-lg z-[9999]">
												<div
													className="px-4 py-2 text-xs text-neutral-500 truncate font-primary border-b border-neutral-200"
													title={displayName}
												>
													{displayName}
												</div>
												<button
													onClick={() => {
														navigate('/profile');
														setShowUserMenu(false);
													}}
													className="w-full text-left px-4 py-2 text-sm text-neutral-700 hover:bg-neutral-50 font-primary transition-colors"
												>
													Profile
												</button>
												{canViewAdminPage && (
													<>
														<button
															onClick={() => {
																navigate('/admin');
																setShowUserMenu(false);
															}}
															className="w-full text-left px-4 py-2 text-sm text-neutral-700 hover:bg-neutral-50 font-primary transition-colors"
														>
															Admin
														</button>
														<button
															onClick={() => {
																navigate('/docs');
																setShowUserMenu(false);
															}}
															className="w-full text-left px-4 py-2 text-sm text-neutral-700 hover:bg-neutral-50 font-primary transition-colors"
														>
															API Docs
														</button>
													</>
												)}
												<div className="border-t border-neutral-200"></div>
												<button
													onClick={handleLogout}
													className="w-full text-left px-4 py-2 text-sm text-neutral-700 hover:bg-neutral-50 font-primary transition-colors"
												>
													Logout
												</button>
											</div>
										)}
									</div>
								</div>
							)}
						</div>

						{/* Mobile menu button (only show if we have navigation menu items) */}
						{shouldShowNavigationMenu && (
							<div className="md:hidden">
								<button
									type="button"
									className="inline-flex items-center justify-center p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500"
									aria-controls="mobile-menu"
									aria-expanded="false"
								>
									<span className="sr-only">Open main menu</span>
									<svg
										className="block h-6 w-6"
										xmlns="http://www.w3.org/2000/svg"
										fill="none"
										viewBox="0 0 24 24"
										stroke="currentColor"
									>
										<path
											strokeLinecap="round"
											strokeLinejoin="round"
											strokeWidth={2}
											d="M4 6h16M4 12h16M4 18h16"
										/>
									</svg>
								</button>
							</div>
						)}
					</div>
				</div>

				{/* Mobile Navigation Menu */}
				{shouldShowNavigationMenu && (
					<div className="md:hidden border-t border-gray-200">
						<div className="px-2 pt-2 pb-3 space-y-1 bg-white">
							{navigationItems.map((item) => (
								<button
									key={item.path}
									onClick={() => handleNavigationClick(item.path)}
									className={`flex items-center space-x-2 w-full px-3 py-2 rounded-md text-base font-medium transition-colors ${
										isActivePath(item.path)
											? 'bg-violet-100 text-black border border-violet-200'
											: 'text-neutral-600 hover:text-gray-900 hover:bg-gray-50'
									}`}
								>
									{item.icon}
									<span>{item.name}</span>
								</button>
							))}
						</div>
					</div>
				)}
			</header>
			{/* Information Banner */}
			{shouldShowBanner && (
				<div
					style={{
						zIndex: 40,
					}}
					className={`border-b h-16 py-4 pl-16 pr-16 fixed top-16 left-0 right-0 ${
						bannerInfo.type === 'success'
							? 'bg-success-50 border-success-200'
							: bannerInfo.type === 'warning'
								? 'bg-warning-50 border-warning-200'
								: 'bg-primary-50 border-primary-200'
					}`}
				>
					<div className="flex items-center justify-between">
						<div className="text-sm text-black flex items-center">
							<Tag className="w-6 w-6 mr-2" />
							{bannerInfo.message}
						</div>
						<Button
							onClick={() => navigate('/subscription')}
							variant="primary"
							size="md"
							className="rounded-full"
							icon={Plus}
						>
							Purchase Credits
						</Button>
					</div>
				</div>
			)}

			{/* First-time User Welcome Banner */}
			{/* {isFirstTimeUserWelcome && (
                <div
                    style={{
                        zIndex: 5,
                    }}
                    className="border-b py-6 pl-16 pr-16 sticky top-16 bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200"
                >
                    <div className="flex items-center justify-between">
                        <div className="text-sm text-black flex items-center">
                            <Sparkles className="w-6 h-6 mr-3 text-blue-600" />
                            <div>
                                <div className="font-semibold text-blue-900 mb-1">
                                    Welcome to RocketFee! 🎉
                                </div>
                                <div className="text-blue-700">
                                    You have 1 free statement analysis. Upload your first document to get started!
                                </div>
                            </div>
                        </div>
                        <button
                            onClick={() => {
                                checkAndStartOnboarding();
                                navigate('/reports');
                            }}
                            className="flex items-center px-6 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white text-sm font-medium rounded-lg transition-all duration-200 shadow-lg hover:shadow-xl"
                        >
                            <Sparkles className="w-4 h-4 mr-2" />
                            Get Started
                        </button>
                    </div>
                </div>
            )} */}

			{/* Main Content */}
			<main
				className={`flex-1 bg-neutral-50 ${shouldShowBanner ? 'pt-32' : 'pt-16'} min-h-0 overflow-hidden`}
			>
				<div className="flex h-full min-h-0 flex-col">{children}</div>
			</main>
		</div>
	);
};

export default Layout;
